import fileIcon from '@/assets/icons/file-icon.svg'
import folderIcon from '@/assets/icons/folder-icon.svg'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useQueryGlobalSearch } from '@/services/folders/queries'
import { GlobalSearchResponse, GlobalSearchResult } from '@/services/folders/types'
import { debounce } from 'lodash'
import { Image, Search, Video } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { useNavigate } from 'react-router'

interface CommandPaletteProps {
    isOpen: boolean
    onClose: () => void
}

const CommandPalette = ({ isOpen, onClose }: CommandPaletteProps) => {
    const [searchQuery, setSearchQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')
    const [page, setPage] = useState(1)
    const [allResults, setAllResults] = useState<GlobalSearchResult[]>([])
    const navigate = useNavigate()
    const scrollContainerRef = useRef<HTMLDivElement>(null)

    // Debounce search input
    const debouncedSearch = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedQuery(value)
                setPage(1)
                setAllResults([])
            }, 300),
        []
    )

    useEffect(() => {
        return () => {
            debouncedSearch.cancel()
        }
    }, [debouncedSearch])

    const { data, isLoading, isFetching } = useQueryGlobalSearch({
        queryParams: {
            query: debouncedQuery,
            page: page.toString(),
            limit: '20',
        },
        enabled: isOpen && debouncedQuery.length > 0,
    })

    const searchData = data as GlobalSearchResponse | undefined

    // Combine results when paginating
    useEffect(() => {
        if (searchData?.results) {
            if (page === 1) {
                setAllResults(searchData.results)
            } else {
                setAllResults(prev => [...prev, ...searchData.results])
            }
        }
    }, [searchData, page])

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('')
            setDebouncedQuery('')
            setPage(1)
            setAllResults([])
        }
    }, [isOpen])

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        setSearchQuery(value)
        debouncedSearch(value)
    }

    const getDocumentIcon = (result: GlobalSearchResult) => {
        if (result.isFolder) {
            return <img src={folderIcon} alt='' className='w-5 h-5' />
        }

        if (result.dataType === 'image') {
            return <Image className='w-5 h-5 text-green-500' />
        }

        if (result.dataType === 'video') {
            return <Video className='w-5 h-5 text-purple-500' />
        }

        return <img src={fileIcon} alt='' className='w-5 h-5' />
    }

    const handleResultClick = (result: GlobalSearchResult) => {
        if (result.isFolder) {
            navigate(`/dashboard/document/${result.id}`)
        } else if (result.dataType === 'node') {
            navigate(`/dashboard/node/file/${result.id}`)
        } else if (result.dataType === 'image' || result.dataType === 'video') {
            navigate(`/dashboard/node/file/${result.id}`)
        } else {
            navigate(`/dashboard/document/file/${result.id}`)
        }
        onClose()
    }

    const renderLoadingState = () => (
        <div className='p-4 space-y-2'>
            {[...Array(5)].map((_, i) => (
                <div key={i} className='flex items-start gap-3 p-3'>
                    <div>
                        <Skeleton width={20} height={20} />
                    </div>
                    <div className='flex-1'>
                        <Skeleton width='60%' height={16} />
                        <Skeleton width='40%' height={12} style={{ marginTop: '8px' }} />
                    </div>
                </div>
            ))}
        </div>
    )

    const renderEmptyState = () => {
        if (!debouncedQuery) {
            return (
                <div className='flex flex-col items-center justify-center py-10 text-gray-600'>
                    <div className='flex items-center justify-center w-12 h-12 mb-4 bg-gray-100 rounded-full'>
                        <Search className='w-6 h-6 opacity-60' />
                    </div>
                    <p className='text-sm font-medium'>Start typing to search your workspace</p>
                    <p className='mt-2 text-xs text-gray-400'>
                        Search across your documents, folders, shared files, and content.
                    </p>
                    <p className='mt-3 text-xs text-gray-400'>
                        Tip: Press <span className='font-semibold'>Cmd + K</span> or{' '}
                        <span className='font-semibold'>Ctrl + K</span> from anywhere to open this search.
                    </p>
                </div>
            )
        }

        return (
            <div className='flex flex-col items-center justify-center py-12 text-gray-500'>
                <Search className='w-12 h-12 mb-4 opacity-50' />
                <p className='text-sm'>No results found for &quot;{debouncedQuery}&quot;</p>
                <p className='mt-2 text-xs text-gray-400'>Try adjusting your search terms</p>
            </div>
        )
    }

    const handleLoadMore = () => {
        if (searchData?.hasMore && !isFetching) {
            setPage(prev => prev + 1)
        }
    }

    // Auto-load more when user scrolls near the bottom
    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const handleScroll = () => {
            if (!searchData?.hasMore || isFetching) return

            const { scrollTop, scrollHeight, clientHeight } = container
            if (scrollTop + clientHeight >= scrollHeight - 80) {
                setPage(prev => prev + 1)
            }
        }

        container.addEventListener('scroll', handleScroll)
        return () => container.removeEventListener('scroll', handleScroll)
    }, [searchData?.hasMore, isFetching])

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='sm:max-w-[700px] p-0 gap-0 overflow-hidden max-h-[80vh] flex flex-col'>
                <div className='p-4 border-b'>
                    <div className='relative'>
                        <Search className='absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2' />
                        <Input
                            type='text'
                            placeholder='Search documents, folders, and content... (Cmd+K)'
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className='h-12 pl-10 text-base'
                            autoFocus
                        />
                    </div>
                </div>

                <div className='flex-1 overflow-y-auto' ref={scrollContainerRef}>
                    {!debouncedQuery ? (
                        renderEmptyState()
                    ) : isLoading && page === 1 ? (
                        renderLoadingState()
                    ) : allResults.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <div className='p-3 space-y-2'>
                            {searchData && (
                                <div className='px-1 pb-1 text-xs text-gray-500'>
                                    {searchData.total} document{searchData.total !== 1 ? 's' : ''} found
                                </div>
                            )}

                            {allResults.map((result, index) => (
                                <div
                                    key={`${result.id}-${index}`}
                                    className='flex items-start gap-3 p-3 transition-all duration-200 bg-white border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                                    onClick={() => handleResultClick(result)}
                                >
                                    <div className='mt-1'>{getDocumentIcon(result)}</div>

                                    <div className='flex-1 min-w-0'>
                                        <div className='flex items-center gap-2 mb-1'>
                                            <h3 className='text-sm font-semibold text-gray-900 truncate'>
                                                {result.name}
                                            </h3>
                                            {result.source === 'shared' && (
                                                <span className='px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-100 text-blue-700 border border-blue-200'>
                                                    Shared
                                                </span>
                                            )}
                                        </div>

                                        {result.snippet && (
                                            <p className='mb-2 text-xs text-gray-600 line-clamp-2'>{result.snippet}</p>
                                        )}

                                        <div className='flex items-center gap-3 text-xs text-gray-400'>
                                            {result.source === 'shared' && (
                                                <span className='flex items-center gap-1'>
                                                    <span className='font-medium'>Owner:</span> {result.ownerName}
                                                </span>
                                            )}
                                            <span className='flex items-center gap-1'>
                                                <span className='font-medium'>Updated:</span>{' '}
                                                {new Date(result.updatedAt).toLocaleDateString('en-US', {
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric',
                                                })}
                                            </span>
                                        </div>
                                    </div>

                                    {result.thumbnailUrl && (
                                        <img
                                            src={result.thumbnailUrl}
                                            alt={result.name}
                                            className='object-cover w-16 h-16 border border-gray-200 rounded-md'
                                        />
                                    )}
                                </div>
                            ))}

                            {/* Infinite scroll trigger and end message */}
                            {searchData && (
                                <div className='p-3 text-center'>
                                    {searchData.hasMore ? (
                                        <div className='flex justify-center'>
                                            {isFetching && page > 1 ? (
                                                <div className='flex items-center gap-3 text-xs text-gray-500'>
                                                    <span className='w-5 h-5 border-2 border-gray-300 rounded-full border-t-transparent animate-spin' />
                                                    <span>Loading more results...</span>
                                                </div>
                                            ) : (
                                                <p
                                                    className='text-xs text-gray-400 cursor-pointer'
                                                    onClick={handleLoadMore}
                                                >
                                                    Scroll for more results...
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        allResults.length > 0 &&
                                        typeof searchData.total === 'number' && (
                                            <p className='py-2 text-xs text-gray-500'>
                                                End of results • {searchData.total}{' '}
                                                {searchData.total === 1 ? 'item' : 'items'} found
                                            </p>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className='p-3 border-t bg-gray-50'>
                    <p className='text-xs text-center text-gray-500'>
                        <kbd className='px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded'>
                            ↑
                        </kbd>
                        <kbd className='px-2 py-1 ml-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded'>
                            ↓
                        </kbd>
                        <span className='mx-2'>to navigate</span>
                        <kbd className='px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded'>
                            Enter
                        </kbd>
                        <span className='mx-2'>to select</span>
                        <kbd className='px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded'>
                            Esc
                        </kbd>
                        <span className='mx-2'>to close</span>
                    </p>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default CommandPalette
